import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  ALTURA_DO_CARTAO,
  LARGURA_DO_CARTAO,
  baixarImagem,
  compartilharOuBaixar,
  desenharCartao,
  imagemDoCanvas,
  nomeDoArquivo,
  temCompartilhamento,
  textoDoCartao,
  type DadosDeCartao,
} from "./cartoes";
import { useDialogoDeAtalhos } from "./useAtalhos";

/**
 * Painel do cartão compartilhável.
 *
 * Mostra a imagem pronta antes de mandar para qualquer lugar: o jogador vê o
 * que vai sair e decide. A imagem é gerada assim que o painel abre, e não no
 * clique, porque a folha de compartilhamento do celular precisa ser chamada
 * dentro do toque do jogador; esperar o desenho ali derrubaria a chamada.
 */
export function CartaoCompartilhavel({
  dados,
  aoFechar,
}: {
  dados: DadosDeCartao;
  aoFechar: () => void;
}): ReactNode {
  const tela = useRef<HTMLCanvasElement | null>(null);
  const imagem = useRef<Blob | null>(null);
  const [pronto, setPronto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;

    async function desenhar(): Promise<void> {
      // Sem esperar as fontes, o primeiro desenho sairia com a fonte de sistema
      // e o cartão ficaria diferente da tela que o gerou.
      try {
        await document.fonts.ready;
      } catch {
        // Navegador sem a API de fontes desenha com o que tiver.
      }
      if (cancelado) return;
      const canvas = tela.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;
      desenharCartao(ctx, dados);
      try {
        const blob = await imagemDoCanvas(canvas);
        if (cancelado) return;
        imagem.current = blob;
        setPronto(true);
      } catch (erro) {
        if (!cancelado) setAviso((erro as Error).message);
      }
    }

    void desenhar();
    return () => {
      cancelado = true;
    };
  }, [dados]);

  const enviar = useCallback(async () => {
    const blob = imagem.current;
    if (!blob) return;
    setOcupado(true);
    try {
      const saida = await compartilharOuBaixar(
        blob,
        nomeDoArquivo(dados),
        "Legacy Football Manager",
        textoDoCartao(dados),
      );
      if (saida === "baixado") setAviso("Imagem salva na pasta de downloads.");
      if (saida === "compartilhado") setAviso("Cartão enviado.");
      if (saida === "cancelado") setAviso(null);
    } catch (erro) {
      setAviso((erro as Error).message);
    } finally {
      setOcupado(false);
    }
  }, [dados]);

  useDialogoDeAtalhos({
    nome: "Cartão compartilhável",
    nivel: 45,
    aoFechar,
    atalhos: { enter: () => void enviar() },
  });

  const compartilha = temCompartilhamento();

  return (
    <div
      role="dialog"
      aria-label="Cartão para compartilhar"
      /*
       * O cartão é aberto de dentro de outro painel, que também fecha ao clique
       * no fundo. Sem segurar o evento aqui, um clique fora do cartão fecharia
       * os dois de uma vez.
       */
      onClick={(evento) => {
        evento.stopPropagation();
        aoFechar();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.82)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--gutter)",
        zIndex: 45,
      }}
    >
      <div
        className="card"
        style={{ width: "min(440px, 100%)", maxHeight: "92vh", overflow: "auto", padding: 18 }}
        onClick={(evento) => evento.stopPropagation()}
      >
        <div className="row" style={{ justifyContent: "space-between", gap: 12 }}>
          <span className="label">Cartão para compartilhar</span>
          <span className="label-muted">feche com Esc</span>
        </div>

        <canvas
          ref={tela}
          width={LARGURA_DO_CARTAO}
          height={ALTURA_DO_CARTAO}
          aria-label="Prévia do cartão"
          style={{
            display: "block",
            width: "100%",
            height: "auto",
            margin: "12px 0",
            borderRadius: "var(--radius)",
            border: "1px solid var(--border)",
            background: "var(--bg-deep)",
          }}
        />

        <p className="muted" style={{ margin: "0 0 12px", fontSize: 11.5 }}>
          {aviso ??
            (compartilha
              ? "A imagem sai em PNG, pronta para o grupo do time."
              : "Este navegador não tem a folha de compartilhamento, então a imagem vai para os downloads.")}
        </p>

        <div className="row" style={{ gap: 8 }}>
          <button className="ghost" onClick={aoFechar}>
            Fechar
          </button>
          {compartilha && (
            <button
              className="ghost"
              disabled={!pronto || ocupado}
              onClick={() => {
                const blob = imagem.current;
                if (!blob) return;
                baixarImagem(blob, nomeDoArquivo(dados));
                setAviso("Imagem salva na pasta de downloads.");
              }}
            >
              Baixar
            </button>
          )}
          <button
            className="primary"
            style={{ flex: 1 }}
            disabled={!pronto || ocupado}
            onClick={() => void enviar()}
          >
            {!pronto ? "Desenhando..." : compartilha ? "Compartilhar" : "Baixar imagem"}
          </button>
        </div>
      </div>
    </div>
  );
}
